import { useEffect, useState } from "react";

import { TBusyTime, TMyClass } from "../@types";
import { apis, callApi } from "../api";
import { useAccount } from "./useAccount";

export type TInstructorAgendaService = {
	listeners: (() => void)[];
	agendas: TMyClass[];
	busyTimes: TBusyTime[];

	addEventListener: (cb: () => void) => void;
	saveAgendas: (agendas: TMyClass[], busyTimes: TBusyTime[]) => void;
	addBusyTime: (newBusyTimes: TBusyTime[]) => void;
	deleteBusyTime: (id: number) => void;
	reschedule: (newAgenda: TMyClass) => void;
	clearData: () => void;
	emitChanges: () => void;
	removeEventListener: (cb: () => void) => void;
};

export const InstructorAgendaService: TInstructorAgendaService = {
	listeners: [],
	agendas: [],
	busyTimes: [],

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	saveAgendas(agendas, busyTimes) {
		this.agendas = agendas;
		this.busyTimes = busyTimes;
		this.emitChanges();
	},

	addBusyTime(newBusyTimes) {
		this.busyTimes = [...this.busyTimes, ...newBusyTimes];
		this.emitChanges();
	},

	deleteBusyTime(id) {
		this.busyTimes = this.busyTimes.filter((busyTime) => busyTime.id !== id);
		this.emitChanges();
	},

	reschedule(newAgenda) {
		this.agendas = this.agendas.filter((agenda) => agenda.id !== newAgenda.id);
		this.agendas = [...this.agendas, newAgenda];
		this.emitChanges();
	},

	clearData() {
		this.agendas = [];
		this.busyTimes = [];
		this.emitChanges();
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},
};

export function useInstructorAgendaInit() {
	const { account } = useAccount();
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		const getTeacherAgenda = async () => {
			try {
				if (!account?.id) {
					return;
				}

				const response = await callApi({
					method: "GET",
					url: `${apis.GET_TEACHER_AGENDA}/${account.id}`,
				});

				if (!response) {
					return;
				}

				InstructorAgendaService.saveAgendas(
					response.bookings,
					response.upcomingSchedules,
				);
			} catch (error) {
			} finally {
				setIsLoading(false);
			}
		};

		if (!account?.id) {
			return;
		}

		getTeacherAgenda();
	}, [account?.id]);

	useEffect(() => {
		return () => {
			InstructorAgendaService.clearData();
		};
	}, []);

	return { isLoading };
}

export function useInstructorAgendas() {
	const [agendas, setAgendas] = useState(InstructorAgendaService.agendas);
	const [busyTimes, setBusyTimes] = useState(InstructorAgendaService.busyTimes);

	useEffect(() => {
		const updateSummaries = () => {
			setAgendas(InstructorAgendaService.agendas);
			setBusyTimes(InstructorAgendaService.busyTimes);
		};

		InstructorAgendaService.addEventListener(updateSummaries);

		return () => {
			InstructorAgendaService.removeEventListener(updateSummaries);
		};
	}, []);

	const addBusyTime = async (body: {
		startDate: string;
		endDate: string;
		blockTime: number;
	}) => {
		try {
			const response = await callApi({
				method: "POST",
				url: apis.CLASSES_SCHEDULE,
				body,
			});

			if (!response) {
				return;
			}

			InstructorAgendaService.addBusyTime(response.schedules);
		} catch (error) {}
	};

	const deleteBusyTime = async (id: number) => {
		try {
			const response = await callApi({
				method: "DELETE",
				url: `${apis.CLASSES_SCHEDULE}/${id}`,
			});

			if (!response) {
				return;
			}

			InstructorAgendaService.deleteBusyTime(id);
		} catch (error) {}
	};

	return { agendas, busyTimes, addBusyTime, deleteBusyTime };
}
